// A single default-off seam. Unsupported callback capabilities stay on the
// existing runtime BEFORE acceptance; accepted runs are never silently retried.
import { serializeVerification } from './verifierDescriptor.js';
export function supervisorContract(config, context = {}) {
  if (!['cloud', 'local'].includes(context.route?.kind)) return { reason: 'provedor não suportado pelo supervisor' };
  const verification = serializeVerification(config.autoCorrect);
  if (verification.reason) return verification;
  if (config.extraTools?.length) return { reason: 'extensões deste turno exigem o executor atual' };
  const { chatFn: _chat, signal: _signal, onEvent: _event, requestApproval: _approval, autoCorrect: _autoCorrect, ...serializable } = config;
  if (verification.value) serializable.verification = verification.value;
  try { structuredClone(serializable); } catch { return { reason: 'capacidade não serializável' }; }
  return { config: { ...serializable, provider: {kind:context.route.kind,isLmStudio:context.route.isLmStudio === true}, providerContext: context.providerContext || {} } };
}
const owners = new Map();
export function observeSupervisorOwnership(bridge = globalThis.window?.apolo?.agentSupervisor) {
  if (!bridge?.list) return () => {};
  let live = true, timer;
  async function refresh() {
    try {
      const runs = await bridge.list();
      if (!live) return;
      for (const row of runs) {
        const sessionId = row.id.slice(0, row.id.lastIndexOf(':'));
        if (['running', 'cancelling'].includes(row.state)) owners.set(sessionId, row.id);
        else if (owners.get(sessionId) === row.id) owners.delete(sessionId);
      }
      // list() is bounded. A completed owner can fall outside its newest rows.
      const listed = new Set(runs.map((row) => row.id));
      for (const [sessionId, id] of owners) {
        if (listed.has(id) || !bridge.status) continue;
        const row = await bridge.status(id);
        if (live && row && !['running', 'cancelling'].includes(row.state)) owners.delete(sessionId);
      }
    } catch { /* transport failure does not transfer execution ownership */ }
    if (live) timer = setTimeout(refresh, 2000);
  }
  refresh();
  return () => { live = false; clearTimeout(timer); };
}
export function externallyOwnedSessions() { return [...owners.keys()]; }
export async function runWithSupervisor(config, fallback, context = {}) {
  const bridge = globalThis.window?.apolo?.agentSupervisor;
  const enabled = globalThis.window?.localStorage?.getItem('apolo:runtime:v2') === '1';
  if (!enabled || !bridge) return fallback(config);
  const contract = supervisorContract(config, context);
  if (contract.reason) {
    window.dispatchEvent(new CustomEvent('apolo:toast', { detail: { kind: 'info', message: `Executor atual: ${contract.reason}.` } }));
    return fallback(config);
  }
  const flags = {};
  const storage = window.localStorage;
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    const value = storage.getItem(key);
    if (/^apolo_[a-z0-9_]+$/.test(key) && ['0', '1', 'true', 'false'].includes(value)) flags[key] = value;
  }
  const accepted = await bridge.start({ ...contract.config, featureFlags: flags });
  if (accepted.disabled) return fallback(config);
  if (!accepted.ok) throw new Error(accepted.error || 'Supervisor não aceitou a execução');
  owners.set(config.sessionId, accepted.runId);
  if (accepted.reattached) throw new Error(`O chat já possui uma execução ativa (${accepted.runId}). Acompanhe em Atividade antes de enviar outro turno.`);
  return waitForSupervisor(config, bridge, accepted.runId);
}

async function waitForSupervisor(config, bridge, runId) {
  const abort = () => bridge.stop(runId).catch(() => {});
  config.signal?.addEventListener('abort', abort, { once: true });
  if (config.signal?.aborted) await abort();
  let cursor = 0, terminal = false, pollDelay = 150;
  try {
    while (true) {
      const events = await bridge.events(runId, cursor);
      for (const event of events) {
        cursor = Math.max(cursor, event.seq);
        const result = await applySupervisorEvent(event, config, bridge, runId);
        if (result) { terminal = true; return result; }
      }
      pollDelay = events.length ? 150 : Math.min(1000, pollDelay * 2);
      await new Promise((resolve) => setTimeout(resolve, pollDelay));
    }
  } catch (error) {
    throw new Error(`Execução ${runId} permanece registrada em Atividade: ${error.message}`);
  } finally {
    if (terminal) owners.delete(config.sessionId);
    config.signal?.removeEventListener('abort', abort);
  }
}

async function applySupervisorEvent(event, config, bridge, runId) {
  if (event.type === 'agent_event') config.onEvent?.(event.event);
  if (event.type === 'approval_required') {
    const allowed = await config.requestApproval?.(event.payload);
    await bridge.approve(runId, event.approvalId, allowed === true);
  }
  if (event.type === 'run_finished') { return event.result || { ok: false, stopReason: event.status === 'cancelled' ? 'aborted' : 'error', error: event.error || event.reason || event.status }; }
  if (event.type === 'run_uncertain') throw new Error('Execução interrompida. Consulte Atividade antes de repetir efeitos.');
  return null;
}
